import { HttpClient } from '@angular/common/http';
import { effect, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { environment } from '@environments/environment';
import { ActorType } from '@models/api-calls/application/application.model';
import { ClientResponse } from '@models/api-calls/clients/client-response.model';
import { Beneficiary } from '@models/api-calls/clients/client.model';
import { ApplicationInformationSubSteps, MainSteps } from '@models/config/app-structure.model';
import { BeneficiariesListFormControls } from '@models/form/form.model';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationService } from '@services/api-calls/application.service';
import { ErrorHandlingService } from '@services/error-handling.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import {
    AddressFormEnum,
    BeneficiariesListFormEnum,
    BeneficiarySubstepFormEnum,
    COUNTRY_CODE_UNITED_STATES,
    OwnerSubstepFormEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';
import { DateUtil } from 'app/utils/date.util';
import { BeneficiaryPercentageValidator } from 'app/validators/beneficiary-percentage.validator';
import { catchError, Observable, tap } from 'rxjs';

const API_URL = environment.apiUrl;

@Injectable({
    providedIn: 'root',
})
export class BeneficiaryService {
    constructor(
        private http: HttpClient,
        private applicationService: ApplicationService,
        private applicationFormService: ApplicationFormService,
        private errorHandlingService: ErrorHandlingService,
        private translateService: TranslateService,
    ) {
        effect(() => {
            this.patchBeneficiariesDataOnForm();
        });

        effect(() => {
            this.lockOwnerFormIfBeneficiariesExist();
        });
    }

    saveAllBeneficiaries(): Observable<unknown> | undefined {
        const applicationId = this.applicationService.application()?.id;
        const parentId = this.applicationService.rawApplication()?.insured_client?.id;

        if (!applicationId || !parentId) {
            return this.errorHandlingService.handleSaveError(new Error(this.translateService.instant('ERRORS.MISSING_BENEFICIARY_DATA')));
        }

        // Check if form was touched
        const beneficiariesArray = this.getBeneficiariesFormArray();

        if (!beneficiariesArray || !beneficiariesArray.touched) {
            return;
        }

        // Check if there are actual changes
        const hasChanges = this.hasChangesComparedToServer();

        if (!hasChanges) {
            return;
        }

        // Build payload: all beneficiaries with all fields
        const beneficiaries = this.buildBeneficiariesPayload(parentId);

        return this.updateAllBeneficiaries(applicationId, beneficiaries).pipe(
            tap(() => {
                this.applicationService.refreshApplicationData.update((n) => n + 1);
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    patchBeneficiariesDataOnForm(): void {
        const beneficiaries: Beneficiary[] = this.applicationService.applicationBeneficiaries();
        if (!beneficiaries) return;

        const beneficiariesArray = this.getBeneficiariesFormArray();
        if (!beneficiariesArray) return;

        // Clear existing form array
        while (beneficiariesArray.length > 0) {
            beneficiariesArray.removeAt(0);
        }

        // Populate form array with server data
        beneficiaries.forEach((beneficiary) => {
            const formGroup = this.applicationFormService.createBeneficiaryListItemForm();

            // Set ID
            formGroup.get(BeneficiariesListFormEnum.ID)?.patchValue(beneficiary.id);

            // Set basic information
            formGroup.get(BeneficiariesListFormEnum.FIRST_NAME)?.patchValue(beneficiary.first_name);
            formGroup.get(BeneficiariesListFormEnum.MIDDLE_NAME)?.patchValue(beneficiary.middle_name);
            formGroup.get(BeneficiariesListFormEnum.LAST_NAME)?.patchValue(beneficiary.last_name);

            // Convert server date string to Date using DateUtil
            const dobDate = DateUtil.fromServer(beneficiary.dob);
            formGroup.get(BeneficiariesListFormEnum.DATE_OF_BIRTH)?.patchValue(dobDate as any);

            formGroup.get(BeneficiariesListFormEnum.SSN)?.patchValue(beneficiary.ssn);

            // Set beneficiary-specific fields
            formGroup.get(BeneficiariesListFormEnum.RELATIONSHIP)?.patchValue(beneficiary.relationship);
            formGroup.get(BeneficiariesListFormEnum.PERCENTAGE)?.patchValue(beneficiary.percentage);
            formGroup.get(BeneficiariesListFormEnum.BENEFICIARY_TYPE)?.patchValue(beneficiary.beneficiary_type);

            // Set contact information
            formGroup.get(BeneficiariesListFormEnum.EMAIL)?.patchValue(beneficiary.email);
            formGroup.get(BeneficiariesListFormEnum.PRIMARY_PHONE)?.patchValue(beneficiary.primary_phone);

            // Set address
            const addressGroup = formGroup.get(BeneficiariesListFormEnum.ADDRESS_GROUP);
            if (addressGroup && beneficiary.address) {
                addressGroup.get(AddressFormEnum.ADDRESS)?.patchValue(beneficiary.address.address);
                addressGroup.get(AddressFormEnum.CITY)?.patchValue(beneficiary.address.city);
                addressGroup.get(AddressFormEnum.STATE_CODE)?.patchValue(beneficiary.address.state_code as any);
                addressGroup.get(AddressFormEnum.ZIP_CODE)?.patchValue(beneficiary.address.zip_code);
                addressGroup.get(AddressFormEnum.ADDRESS2)?.patchValue(beneficiary.address.address2);

                // Build FULL_ADDRESS from individual fields for display
                const fullAddressParts = [
                    beneficiary.address.address,
                    beneficiary.address.address2,
                    beneficiary.address.city,
                    beneficiary.address.state_code,
                    beneficiary.address.zip_code,
                ].filter(Boolean);
                addressGroup.get(AddressFormEnum.FULL_ADDRESS)?.patchValue(fullAddressParts.join(', '), { emitEvent: false });
            }

            beneficiariesArray.push(formGroup);
        });

        // If no beneficiaries from server, add one default empty beneficiary
        if (beneficiariesArray.length === 0) {
            beneficiariesArray.push(this.applicationFormService.createBeneficiaryListItemForm());
        }

        // Mark as pristine to match server state
        beneficiariesArray.markAsPristine();
        beneficiariesArray.markAsUntouched();
    }

    private lockOwnerFormIfBeneficiariesExist(): void {
        const beneficiaries = this.applicationService.applicationBeneficiaries();
        if (!beneficiaries || beneficiaries.length === 0) return;

        const ownerForm = this.applicationFormService.applicationForm
            .get(MainSteps.APPLICATION_INFORMATION)
            ?.get(ApplicationInformationSubSteps.OWNER);

        if (!ownerForm) return;

        const isClientOwnerControl = ownerForm.get(OwnerSubstepFormEnum.IS_CLIENT_OWNER);
        if (!isClientOwnerControl) return;

        if (isClientOwnerControl.value !== YesNoOptionsEnum.YES) {
            isClientOwnerControl.patchValue(YesNoOptionsEnum.YES);
        }
    }

    hasChangesComparedToServer(): boolean {
        const beneficiariesArray = this.getBeneficiariesFormArray();
        if (!beneficiariesArray) return false;

        const formBeneficiaries = beneficiariesArray.controls.map((formGroup) => this.createBeneficiaryDataFromForm(formGroup, null));

        const serverBeneficiaries = this.applicationService.rawApplication()?.app_beneficiaries || [];

        // If server has none and form only has the default empty row, no changes
        if (serverBeneficiaries.length === 0 && formBeneficiaries.length === 1 && !formBeneficiaries[0].id && !formBeneficiaries[0].first_name) {
            return false;
        }

        // Different count = changes
        if (formBeneficiaries.length !== serverBeneficiaries.length) return true;

        // Compare each beneficiary
        for (let i = 0; i < formBeneficiaries.length; i++) {
            const formBen = formBeneficiaries[i];
            const serverId = formBen.id;

            if (!serverId) {
                // New beneficiary (no ID) = changes
                return true;
            }

            const serverBen = serverBeneficiaries.find((b) => b.id === serverId);
            if (!serverBen) {
                // ID exists in form but not on server (shouldn't happen) = changes
                return true;
            }

            // Compare field by field
            if (this.beneficiaryHasChanges(formBen, serverBen)) {
                return true;
            }
        }

        return false;
    }

    private beneficiaryHasChanges(formData: Partial<ClientResponse>, serverData: ClientResponse): boolean {
        const fieldsToCompare: (keyof ClientResponse)[] = [
            'first_name',
            'middle_name',
            'last_name',
            'dob',
            'ssn',
            'email',
            'primary_phone',
            'relationship',
            'percentage',
            'beneficiary_type',
            'address',
            'city',
            'state_code',
            'zip_code',
            'address2',
        ];

        return fieldsToCompare.some((field) => formData[field] !== serverData[field]);
    }

    private buildBeneficiariesPayload(parentId: number): Partial<ClientResponse>[] {
        const beneficiariesArray = this.getBeneficiariesFormArray();
        if (!beneficiariesArray) return [];

        return beneficiariesArray.controls.map((formGroup) => this.createBeneficiaryDataFromForm(formGroup, parentId));
    }

    private createBeneficiaryDataFromForm(
        formGroup: FormGroup<BeneficiariesListFormControls>,
        parentId: number | null,
    ): Partial<ClientResponse> {
        const addressGroup = formGroup.get(BeneficiariesListFormEnum.ADDRESS_GROUP);
        const dobValue = formGroup.get(BeneficiariesListFormEnum.DATE_OF_BIRTH)?.value;
        const addressLine = addressGroup?.get(AddressFormEnum.ADDRESS)?.value || null;
        const city = addressGroup?.get(AddressFormEnum.CITY)?.value || null;
        const stateCode = addressGroup?.get(AddressFormEnum.STATE_CODE)?.value || null;
        const zipCode = addressGroup?.get(AddressFormEnum.ZIP_CODE)?.value || null;
        const address2 = addressGroup?.get(AddressFormEnum.ADDRESS2)?.value || null;
        const hasAnyAddressField = !!(addressLine || city || stateCode || zipCode || address2);
        // Only emit a country_code when the user actually provided some address data —
        // otherwise downstream systems see country=US with all other address fields
        // blank and treat it as a partially-populated address.
        const countryCode = hasAnyAddressField ? COUNTRY_CODE_UNITED_STATES : undefined;

        const data: Partial<ClientResponse> & { completeAddress?: string } = {
            // TODO: confirm with Cosmin if the role is actually not needed (throws error if included)
            // role: ActorType.BENEFICIARY,
            id: formGroup.get(BeneficiariesListFormEnum.ID)?.value || null,
            first_name: formGroup.get(BeneficiariesListFormEnum.FIRST_NAME)?.value,
            middle_name: formGroup.get(BeneficiariesListFormEnum.MIDDLE_NAME)?.value || null,
            last_name: formGroup.get(BeneficiariesListFormEnum.LAST_NAME)?.value,
            dob: dobValue && typeof dobValue === 'object' ? DateUtil.toServer(dobValue as Date) : null,
            ssn: formGroup.get(BeneficiariesListFormEnum.SSN)?.value || null,
            email: formGroup.get(BeneficiariesListFormEnum.EMAIL)?.value || null,
            primary_phone: formGroup.get(BeneficiariesListFormEnum.PRIMARY_PHONE)?.value || null,
            relationship: formGroup.get(BeneficiariesListFormEnum.RELATIONSHIP)?.value,
            percentage: formGroup.get(BeneficiariesListFormEnum.PERCENTAGE)?.value,
            beneficiary_type: formGroup.get(BeneficiariesListFormEnum.BENEFICIARY_TYPE)?.value,
            address: addressLine,
            city,
            state_code: stateCode,
            zip_code: zipCode,
            address2,
            // TODO: remove this hardcoded value when the google address is implemented
            completeAddress: hasAnyAddressField ? '300 College Ave, Los Gatos, CA 95030, USA' : undefined,
            country_code: countryCode,
        };

        // Include parent_id if provided
        if (parentId !== null) {
            data.parent_id = parentId;
        }

        return data;
    }

    private getBeneficiariesFormArray(): FormArray<FormGroup<BeneficiariesListFormControls>> | null {
        const array = this.applicationFormService.applicationForm.get([
            MainSteps.APPLICATION_INFORMATION,
            ApplicationInformationSubSteps.BENEFICIARY,
            BeneficiarySubstepFormEnum.BENEFICIARIES_LIST,
        ]);

        return array as FormArray<FormGroup<BeneficiariesListFormControls>> | null;
    }

    private updateAllBeneficiaries(applicationId: number, beneficiaries: Partial<ClientResponse>[]): Observable<ClientResponse[]> {
        const url = `${API_URL}/applications/${applicationId}/clients/beneficiaries`;
        return this.http.put<ClientResponse[]>(url, { items: beneficiaries, role: ActorType.BENEFICIARY });
    }
}
